import classNames from 'classnames';
import React, {useRef, forwardRef, useImperativeHandle} from 'react';
import Button from '../../../../incase-domain/components/button/Button';
import {COMPARISON_TYPE, useCharacterCounter} from '../../../../hooks/useCharacterCounter';

export interface CharacterCounterProps {
    value: string;
    onChange: (value: string) => void;
    onBlur?: (value: string) => void;
    onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    placeholder?: string;
    className?: string;
    maxLength?: number;
    onLimitChange?: (isOverLimit: boolean) => void;
    validator?: (val: string) => boolean;
    disabled?: boolean;
    comparisonType?: COMPARISON_TYPE;
    dataCy?: string;
    leading?: string;
    trailing?: JSX.Element;
    inlineMaxLength?: boolean;
    enableSubmitByEnter?: boolean;
    onEnterPress?: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    error?: string;
}

const InputWithCharacterCounter = forwardRef<HTMLInputElement, CharacterCounterProps>(function InputWithCharacterCounter(props, ref) {
    const {
        value,
        onChange,
        onBlur,
        validator,
        onKeyUp,
        placeholder,
        className,
        maxLength,
        onLimitChange,
        disabled,
        comparisonType,
        dataCy,
        leading,
        trailing,
        inlineMaxLength,
        enableSubmitByEnter,
        onEnterPress,
        error,
    } = props;

    const innerRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

    const {isOverLimit, hasError, setHasError} = useCharacterCounter({
        value,
        maxLength,
        comparisonType,
        onLimitChange,
        validator,
    });

    function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
        if (!enableSubmitByEnter) return;
        if (event.key === 'Enter') {
            event.preventDefault();
            if (onEnterPress) {
                onEnterPress(event);
            } else {
                event.currentTarget.blur();
            }
        }
    }

    return (
        <>
            <div className={classNames(className, {'is-error': hasError})}>
                {leading && (
                    <Button
                        className="c-sidepanel__input__leading"
                        leading={leading}
                        size="xs"
                        variant="ghost"
                        square
                        disablePointerEvents
                    />
                )}

                <input
                    ref={innerRef}
                    type="text"
                    placeholder={placeholder}
                    value={value}
                    maxLength={maxLength}
                    onChange={(e) => {
                        onChange(e.target.value);
                        if (hasError && validator) setHasError(!validator(e.target.value));
                    }}
                    onBlur={(e) => {
                        onBlur?.(e.target.value);
                        if (validator) setHasError(!validator(e.target.value));
                    }}
                    onKeyDown={handleKeyDown}
                    onKeyUp={onKeyUp}
                    disabled={disabled}
                    data-cy={dataCy}
                    className="c-sidepanel__input_field"
                />

                <div className="c-sidepanel__input__trailing">{trailing}</div>

                {inlineMaxLength && maxLength ? (
                    <div
                        className={classNames('c-characterCounter__inline', {
                            'c-characterCounter--alert': isOverLimit || hasError,
                            'c-characterCounter--hidden': disabled,
                        })}
                    >
                        {value.length}/{maxLength}
                    </div>
                ) : null}
            </div>

            <div className="u-flex u-gap-1 u-v-center u-h-between">
                {error ? <div className="c-characterCounter c-characterCounter--alert">{error}</div> : null}

                {!inlineMaxLength && maxLength ? (
                    <div
                        className={classNames('c-characterCounter', {
                            'c-characterCounter--alert': isOverLimit || hasError,
                            'u-hide': disabled,
                        })}
                    >
                        {value.length}/{maxLength}
                    </div>
                ) : null}
            </div>
        </>
    );
});

export default InputWithCharacterCounter;
